import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useConsentService } from 'hooks/use-consent-service';
import type { ConsentValidation, ConsentCaptureData } from 'services/consent-service';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

export type ConsentContextValue = {
  /** Whether consent checkboxes must be shown for this user. */
  isRequired: boolean;

  /** Checkbox state — read by ConsentCheckboxGroup, written by the molecule. */
  privacyConsented: boolean;
  marketingConsented: boolean;
  onPrivacyChange: (checked: boolean) => void;
  onMarketingChange: (checked: boolean) => void;

  /**
   * Call before form submit. Returns `{ ok: true }` when not required or
   * when both boxes are ticked; `{ ok: false, reason }` otherwise.
   */
  validate(): ConsentValidation;

  /**
   * Call after successful form submit.
   * No-ops silently when consent is not required.
   * `source` is bound internally — pass only user/listing data.
   */
  capture(data: Omit<ConsentCaptureData, 'source'>): Promise<void>;

  labels: {
    privacyPolicy: React.JSX.Element;
    marketingCommunications: string;
  };
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ConsentContext = createContext<ConsentContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type ConsentProviderProps = {
  children: React.ReactNode;
  /**
   * The button label shown in consent text, e.g. "Continue" or "Submit".
   * Forwarded to i18n interpolation so the text reads
   * "By clicking '{{buttonText}}', you agree to…"
   */
  buttonText?: string;
};

export function ConsentProvider({ children, buttonText }: ConsentProviderProps) {
  const { service, labels } = useConsentService(buttonText);
  const [privacyConsented, setPrivacyConsented] = useState(false);
  const [marketingConsented, setMarketingConsented] = useState(false);

  const validate = useCallback((): ConsentValidation => {
    return service.validate(privacyConsented && marketingConsented);
  }, [service, privacyConsented, marketingConsented]);

  const value: ConsentContextValue = useMemo(() => ({
    isRequired:        service.isRequired,
    privacyConsented,
    marketingConsented,
    onPrivacyChange:   setPrivacyConsented,
    onMarketingChange: setMarketingConsented,
    validate,
    capture:           service.capture,
    labels,
  }), [service.isRequired, service.capture, privacyConsented, marketingConsented, validate, labels]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the consent context for the nearest `ConsentProvider`.
 * Throws if called outside a provider — this is intentional: a component
 * that uses consent must be wrapped in a provider.
 */
export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within a ConsentProvider');
  return ctx;
}
